import React, { useMemo, useState } from "react";
import { Box, Text, UnstyledButton } from "@mantine/core";
import { GRID_TOKENS as tokens } from "../tokens";
import { HOUR_RAIL_W } from "../tokens";
import {
  DayHours,
  GridWindow,
  hourMarks,
  layoutDayColumn,
  MobileAppt,
  offHoursSpans,
  PlacedBlock,
  snapPressMinute,
} from "./geometry";
import { MobileBlock } from "./MobileBlock";
import { MobileSlotMenu, slotMenuPlacement } from "./MobileSlotMenu";
import { StaffFace } from "./StaffFace";
import styles from "./MobileGrid.module.css";

export interface MobileStaffColumn {
  id: string;
  name: string;
  initials: string;
  photo?: string | null;
}

/** A day-grid column: the provider plus their hours on the viewed day. */
export interface MobileDayColumn extends MobileStaffColumn {
  /**
   * Their working hours that day (override, else weekly schedule); the rest
   * of the column is hatched. Null = not working: the whole column hatched.
   */
  hours: DayHours | null;
}

export interface SlotPress {
  staffId: string;
  startMin: number;
}

export interface MobileDayGridProps {
  columns: MobileDayColumn[];
  /** The viewed day's appointments + time blocks (any staff). */
  appts: MobileAppt[];
  window: GridWindow;
  /** Current minute when the viewed day is today, else null. */
  nowMin: number | null;
  onBlockTap: (appt: MobileAppt) => void;
  onStaffHead: (staffId: string) => void;
  onSlotAppointment: (slot: SlotPress) => void;
  onSlotTimeBlock: (slot: SlotPress) => void;
}

interface SlotMenuState extends SlotPress {
  left: number;
  top: number;
}

/**
 * Day grid: one column per provider (flexing at one or two, 148/118px and
 * horizontally scrolling at three or more), sticky hour rail + staff header,
 * hour rules, blocks, now line, each provider's off-hours hatch, and the
 * empty-slot press menu. The hatch is visual only (like desktop): a press on
 * off-hours time still opens the menu.
 */
export const MobileDayGrid: React.FC<MobileDayGridProps> = ({
  columns,
  appts,
  window,
  nowMin,
  onBlockTap,
  onStaffHead,
  onSlotAppointment,
  onSlotTimeBlock,
}) => {
  const ppm = tokens.ppmDay;
  const colW = tokens.staffColW;
  const [slotMenu, setSlotMenu] = useState<SlotMenuState | null>(null);

  const laneH = (window.endMin - window.startMin) * ppm;
  const many = columns.length > 2;
  const rowWidth = many ? HOUR_RAIL_W + columns.length * colW : "100%";
  const colFlex = many ? `0 0 ${colW}px` : "1 1 0";
  const marks = useMemo(() => hourMarks(window, ppm), [window, ppm]);

  const blocksByStaff = useMemo(() => {
    const map = new Map<string, PlacedBlock[]>();
    columns.forEach((c) => {
      map.set(
        c.id,
        layoutDayColumn(
          appts.filter((a) => a.staffId === c.id),
          window,
          ppm,
        ),
      );
    });
    return map;
  }, [columns, appts, window, ppm]);

  const hatchesByStaff = useMemo(
    () =>
      new Map(columns.map((c) => [c.id, offHoursSpans(c.hours, window, ppm)])),
    [columns, window, ppm],
  );

  const handleLanePress = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-block]")) return;
    if (columns.length === 0) return;
    if (slotMenu) {
      setSlotMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const colEl = target.closest("[data-staff]");
    const staffId = colEl?.getAttribute("data-staff") || columns[0].id;
    const startMin = snapPressMinute(e.clientY - rect.top, window, ppm);
    setSlotMenu({
      staffId,
      startMin,
      ...slotMenuPlacement(startMin, e.clientX, rect, window, ppm),
    });
  };

  const pick = (fn: (slot: SlotPress) => void) => () => {
    if (!slotMenu) return;
    const { staffId, startMin } = slotMenu;
    setSlotMenu(null);
    fn({ staffId, startMin });
  };

  return (
    <Box className={styles.inner} data-testid="mobile-day-grid">
      <Box
        className={styles.headRow}
        style={{ width: rowWidth }}
        data-testid="mobile-day-head"
      >
        <Box className={styles.headSpacer} />
        {columns.map((c) => (
          <UnstyledButton
            key={c.id}
            className={styles.staffHead}
            style={{ flex: colFlex }}
            onClick={() => onStaffHead(c.id)}
            data-testid={`mobile-staff-head-${c.id}`}
          >
            <Text span className={styles.staffIni}>
              <StaffFace photo={c.photo} initials={c.initials} />
            </Text>
            <Text span className={styles.staffName}>
              {c.name}
            </Text>
          </UnstyledButton>
        ))}
      </Box>
      <Box className={styles.gridRow} style={{ width: rowWidth }}>
        <Box className={styles.rail} style={{ height: laneH }}>
          {marks.map((m) => (
            <Text
              span
              key={m.minute}
              className={styles.hourLabel}
              style={{ top: m.labelTop, fontSize: tokens.hourLabelPx }}
            >
              {m.label}
            </Text>
          ))}
        </Box>
        <Box
          className={styles.lane}
          style={{ height: laneH, cursor: "copy" }}
          onClick={handleLanePress}
          data-testid="mobile-day-lane"
        >
          {/* Off-hours hatch: its own row of columns under the hour rules. */}
          <Box className={styles.hatchRow}>
            {columns.map((c) => (
              <Box
                key={c.id}
                className={styles.hatchCol}
                style={{ flex: colFlex }}
              >
                {(hatchesByStaff.get(c.id) || []).map((h) => (
                  <Box
                    key={h.top}
                    className={styles.hatch}
                    data-testid={`mobile-day-hatch-${c.id}`}
                    style={{ top: h.top, height: h.height }}
                  />
                ))}
              </Box>
            ))}
          </Box>
          {marks.map((m) => (
            <Box
              key={m.minute}
              className={styles.hourLine}
              data-edge={m.edge}
              style={{ top: m.top }}
            />
          ))}
          {columns.map((c) => (
            <Box
              key={c.id}
              className={styles.col}
              data-staff={c.id}
              data-working={c.hours !== null}
              data-testid={`mobile-day-col-${c.id}`}
              style={{ flex: colFlex, height: laneH }}
            >
              {(blocksByStaff.get(c.id) || []).map((p) => (
                <MobileBlock
                  key={p.appt.id}
                  placed={p}
                  variant="day"
                  ppm={ppm}
                  onTap={() => onBlockTap(p.appt)}
                />
              ))}
            </Box>
          ))}
          {nowMin !== null &&
            nowMin >= window.startMin &&
            nowMin <= window.endMin && (
              <Box
                className={styles.nowLine}
                data-testid="mobile-now-line"
                style={{
                  left: -4,
                  right: 0,
                  top: (nowMin - window.startMin) * ppm,
                }}
              >
                <Box className={styles.nowDot} />
              </Box>
            )}
          {slotMenu && (
            <MobileSlotMenu
              left={slotMenu.left}
              top={slotMenu.top}
              onAppointment={pick(onSlotAppointment)}
              onTimeBlock={pick(onSlotTimeBlock)}
              onClose={() => setSlotMenu(null)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
